import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, doc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { createUserAsAdmin } from '../utils/createUser';
import './AdminUsersView.css';
import './SuperAdminDetailPages.css';
import StatCards from './StatCards';

function SuperAdminUsersView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailChildren, setDetailChildren] = useState([]);
  const [detailChildrenLoading, setDetailChildrenLoading] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Teacher',
    teacherType: 'regular',
    specialtySubjects: []
  });
  const [addUserError, setAddUserError] = useState('');

  const availableSubjects = ["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"];

  const loadUsers = useCallback(async () => {
    console.log('🔥 AdminUsers: Loading users, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      let usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Super Admin: no school selected = national view, load all users
      const sid = (adminData?.schoolId || '').toLowerCase();
      if (sid) {
        usersData = usersData.filter(u => (u.schoolId || '').toLowerCase() === sid);
      }
      console.log('🔥 AdminUsers: Users loaded', usersData.length, usersData);
      setUsers(usersData);
    } catch (error) {
      console.error('🔥 AdminUsers: Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Lazily resolve a parent user's linked children (match linkedChildrenIds
  // against student.studentId, doc id, and uuid — case-insensitive)
  useEffect(() => {
    if (!detailUser || detailUser.role !== 'Parent') {
      setDetailChildren([]);
      return;
    }
    const ids = (detailUser.linkedChildrenIds || []).map(x => String(x).toLowerCase());
    if (ids.length === 0) {
      setDetailChildren([]);
      return;
    }
    console.log('🔥 AdminUsers: Resolving linked children for parent', detailUser.id);
    setDetailChildrenLoading(true);
    (async () => {
      try {
        const classesSnap = await getDocs(collection(db, 'classes'));
        const found = [];
        for (const classDoc of classesSnap.docs) {
          const studentsSnap = await getDocs(collection(db, 'classes', classDoc.id, 'students'));
          studentsSnap.docs.forEach(d => {
            const s = { id: d.id, ...d.data() };
            const keys = [s.studentId, s.id, s.uuid].filter(Boolean).map(x => String(x).toLowerCase());
            if (keys.some(k => ids.includes(k))) {
              found.push(s);
            }
          });
        }
        console.log('🔥 AdminUsers: Linked children resolved', found.length);
        setDetailChildren(found);
      } catch (error) {
        console.error('🔥 AdminUsers: Error resolving linked children:', error);
      } finally {
        setDetailChildrenLoading(false);
      }
    })();
  }, [detailUser]);

  // Resolve schoolId -> school name (match doc id, uuid field, or name; case-insensitive)
  const resolveSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const sid = String(schoolId).toLowerCase();
    const match = saSchools.find(s =>
      String(s.id).toLowerCase() === sid ||
      String(s.uuid || '').toLowerCase() === sid ||
      String(s.name || '').toLowerCase() === sid
    );
    return match ? (match.name || match.id) : String(schoolId);
  };

  const getFilteredUsers = () => {
    let filtered = users;
    
    if (roleFilter !== 'All') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    if (searchText) {
      filtered = filtered.filter(user =>
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    return filtered;
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      // Keep the open detail page in sync if this user is being viewed
      if (detailUser && detailUser.id === userId) {
        setDetailUser({ ...detailUser, role: newRole });
      }
      loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return false;
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: false,
        deletedAt: new Date().toISOString()
      });
      loadUsers();
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
      return false;
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      teacherType: user.teacherType || 'regular',
      specialtySubject: user.specialtySubject || ''
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      await updateDoc(doc(db, 'users', editingUser.id), {
        teacherType: editForm.teacherType,
        specialtySubject: editForm.teacherType === 'subject' ? editForm.specialtySubject : null
      });
      setEditingUser(null);
      setEditForm({});
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleAddUser = async () => {
    setAddUserError('');
    
    if (!addUserForm.username || !addUserForm.email || !addUserForm.password) {
      setAddUserError('Please fill in all required fields');
      return;
    }
    
    if (addUserForm.role === 'Teacher' && addUserForm.teacherType === 'subject' && addUserForm.specialtySubjects.length === 0) {
      setAddUserError('At least one specialty subject is required for subject teachers');
      return;
    }
    
    try {
      // Secondary-app auth so creating the account does NOT sign the super admin out.
      const uid = await createUserAsAdmin(addUserForm.email, addUserForm.password);

      const nameParts = addUserForm.username.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const userData = {
        id: uid,
        uuid: crypto.randomUUID(),
        username: addUserForm.username,
        firstName,
        lastName,
        email: addUserForm.email,
        role: addUserForm.role,
        teacherType: addUserForm.role === 'Teacher' ? addUserForm.teacherType : null,
        specialtySubjects: addUserForm.role === 'Teacher' && addUserForm.teacherType === 'subject' ? addUserForm.specialtySubjects : [],
        assignedClassIds: [],
        schoolId: adminData?.schoolId || null,
        isActive: true,
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', uid), userData);

      if (addUserForm.role === 'Teacher') {
        const teacherData = {
          id: uid,
          email: addUserForm.email,
          fullName: addUserForm.username,
          teacherType: addUserForm.teacherType,
          specialtySubjects: addUserForm.teacherType === 'subject' ? addUserForm.specialtySubjects : [],
          createdAt: new Date().toISOString(),
          isActive: true,
          schoolId: adminData?.schoolId || null
        };
        await setDoc(doc(db, 'teachers', uid), teacherData);
      }

      if (addUserForm.role === 'Parent') {
        await setDoc(doc(db, 'parents', uid), {
          id: uid,
          userId: uid,
          fullName: addUserForm.username,
          firstName,
          lastName,
          email: addUserForm.email,
          phoneNumber: '',
          linkedChildrenIds: [],
          isActive: true,
          schoolId: adminData?.schoolId || null,
          createdAt: new Date().toISOString()
        });
      }
      
      setShowAddUserModal(false);
      setAddUserForm({
        username: '',
        email: '',
        password: '',
        role: 'Teacher',
        teacherType: 'regular',
        specialtySubjects: []
      });
      loadUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      setAddUserError(error.message || 'Failed to add user');
    }
  };

  const handleCancelAddUser = () => {
    setShowAddUserModal(false);
    setAddUserForm({
      username: '',
      email: '',
      password: '',
      role: 'Teacher',
      teacherType: 'regular',
      specialtySubjects: []
    });
    setAddUserError('');
  };

  const getRoleIcon = (role) => {
    const icons = {
      'Admin': '👑',
      'Teacher': '👨‍🏫',
      'Parent': '👨‍👩‍👧'
    };
    return icons[role] || '👤';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Admin': '#14532d',
      'Teacher': '#22c55e',
      'Parent': '#22c55e'
    };
    return colors[role] || '#666';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers();

  const userRoleCount = (r) => users.filter(u => (u.role || '').toLowerCase() === r).length;

  // Full-screen user detail page — replaces the list (not an overlay)
  if (detailUser) {
    const u = detailUser;
    const displayName = u.username || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
    return (
      <div className="detail-page">
        <button className="dp-back" onClick={() => setDetailUser(null)}>
          ◄ Back to Users
        </button>

        <div className="dp-hero">
          <div className="dp-avatar" style={{ background: `${getRoleColor(u.role)}20`, color: getRoleColor(u.role) }}>
            {getRoleIcon(u.role)}
          </div>
          <div className="dp-hero-text">
            <h2>{displayName}</h2>
            <p>{u.email || 'No email'}</p>
            <span className="dp-badge" style={{ background: `${getRoleColor(u.role)}20`, color: getRoleColor(u.role) }}>
              {u.role || 'No role'}
            </span>
            <span className={`dp-badge ${u.isActive === false ? 'red' : 'green'}`}>
              {u.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>

        <div className="dp-card">
          <h3>User Information</h3>
          <div className="dp-grid">
            <div className="dp-field">
              <div className="dp-label">Name</div>
              <div className="dp-value">{displayName}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Email</div>
              <div className="dp-value">{u.email || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Username</div>
              <div className="dp-value">{u.username || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">School</div>
              <div className="dp-value">{resolveSchoolName(u.schoolId)}</div>
            </div>
            {u.role === 'Teacher' && (
              <div className="dp-field">
                <div className="dp-label">Teacher Type</div>
                <div className="dp-value">{u.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}</div>
              </div>
            )}
            {u.role === 'Teacher' && (
              <div className="dp-field">
                <div className="dp-label">Specialty Subjects</div>
                <div className="dp-value">
                  {(u.specialtySubjects && u.specialtySubjects.length > 0)
                    ? u.specialtySubjects.join(', ')
                    : (u.specialtySubject || 'None')}
                </div>
              </div>
            )}
            <div className="dp-field">
              <div className="dp-label">Status</div>
              <div className="dp-value">{u.isActive === false ? 'Inactive' : 'Active'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Created</div>
              <div className="dp-value">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div className="dp-field full">
              <div className="dp-label">User ID</div>
              <div className="dp-value">{u.id}</div>
            </div>
          </div>
        </div>

        {u.role === 'Parent' && (
          <div className="dp-card">
            <h3>Linked Children ({detailChildrenLoading ? '…' : detailChildren.length})</h3>
            {detailChildrenLoading ? (
              <p className="dp-empty">Resolving linked children...</p>
            ) : detailChildren.length === 0 ? (
              <p className="dp-empty">No linked children</p>
            ) : (
              detailChildren.map(child => (
                <div key={child.id} className="dp-row">
                  <span className="dp-row-main">{child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'Unknown'}</span>
                  <span className="dp-row-sub">{child.studentId || ''}</span>
                </div>
              ))
            )}
          </div>
        )}

        <div className="dp-actions">
          <select
            value={u.role}
            onChange={(e) => handleUpdateRole(u.id, e.target.value)}
            className="dp-role-select"
          >
            <option value="Admin">Admin</option>
            <option value="Teacher">Teacher</option>
            <option value="Parent">Parent</option>
            <option value="Scanner">Scanner</option>
            <option value="Super Admin">Super Admin</option>
          </select>
          {u.role === 'Teacher' && (
            <button
              type="button"
              className="dp-btn-primary"
              onClick={() => { const user = u; setDetailUser(null); handleEditUser(user); }}
            >
              Edit Teacher Type
            </button>
          )}
          <button
            type="button"
            className="dp-btn-danger"
            onClick={async () => { if (await handleDeleteUser(u.id)) setDetailUser(null); }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-view">
      <div className="view-header">
        <h2>Users</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button onClick={() => setShowAddUserModal(true)} className="add-user-button">
          + Add User
        </button>
      </div>
      <StatCards items={[['Parents',userRoleCount('parent')],['Teachers',userRoleCount('teacher')],['Scanners',userRoleCount('scanner')],['Admins',userRoleCount('admin')],['Total',users.length]]} charts={[{title:'Users by Role',type:'pie',data:['Parent','Teacher','Scanner','Admin'].map(r => ({name:r,value:userRoleCount(r.toLowerCase())}))}]} />

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search users..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <div className="role-filters">
        <button
          className={`role-filter-btn ${roleFilter === 'All' ? 'active' : ''}`}
          onClick={() => setRoleFilter('All')}
        >
          All ({users.length})
        </button>
        <button
          className={`role-filter-btn ${roleFilter === 'Admin' ? 'active' : ''}`}
          onClick={() => setRoleFilter('Admin')}
        >
          Admin ({users.filter(u => u.role === 'Admin').length})
        </button>
        <button
          className={`role-filter-btn ${roleFilter === 'Teacher' ? 'active' : ''}`}
          onClick={() => setRoleFilter('Teacher')}
        >
          Teacher ({users.filter(u => u.role === 'Teacher').length})
        </button>
        <button
          className={`role-filter-btn ${roleFilter === 'Parent' ? 'active' : ''}`}
          onClick={() => setRoleFilter('Parent')}
        >
          Parent ({users.filter(u => u.role === 'Parent').length})
        </button>
      </div>

      <p className="count-text">Users: {filteredUsers.length}</p>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👤</div>
          <h3>No Users</h3>
          <p>No users found</p>
        </div>
      ) : (
        <div className="users-list">
          {filteredUsers.map(user => (
            <div key={user.id} className="user-card" onClick={() => setDetailUser(user)} style={{ cursor: 'pointer' }}>
              <div className="user-avatar" style={{ background: `${getRoleColor(user.role)}20` }}>
                <span style={{ color: getRoleColor(user.role) }}>
                  {getRoleIcon(user.role)}
                </span>
              </div>
              <div className="user-info">
                <h4>{user.username}</h4>
                <p>{user.email}</p>
                <span className="role-badge" style={{ background: `${getRoleColor(user.role)}20`, color: getRoleColor(user.role) }}>
                  {user.role}
                </span>
              </div>
              <div className="user-actions">
                <select
                  value={user.role}
                  onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="role-select"
                >
                  <option value="Admin">Admin</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Parent">Parent</option>
                  <option value="Scanner">Scanner</option>
                  <option value="Super Admin">Super Admin</option>
                </select>
                {user.role === 'Teacher' && (
                  <button
                    onClick={() => handleEditUser(user)}
                    className="edit-button"
                  >
                    Edit Teacher Type
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editingUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Teacher Type</h3>
            <div className="form-group">
              <label>Teacher Type</label>
              <select
                value={editForm.teacherType}
                onChange={(e) => setEditForm({ ...editForm, teacherType: e.target.value })}
              >
                <option value="regular">Regular (Home Classroom)</option>
                <option value="subject">Subject/Activity Teacher</option>
              </select>
            </div>
            {editForm.teacherType === 'subject' && (
              <div className="form-group">
                <label>Specialty Subject</label>
                <select
                  value={editForm.specialtySubject}
                  onChange={(e) => setEditForm({ ...editForm, specialtySubject: e.target.value })}
                >
                  <option value="">Select Subject</option>
                  {availableSubjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="modal-actions">
              <button onClick={handleCancelEdit} className="cancel-button">Cancel</button>
              <button onClick={handleSaveUser} className="save-button">Save</button>
            </div>
          </div>
        </div>
      )}

      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add New User</h3>
            {addUserError && <div className="error-message">{addUserError}</div>}
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={addUserForm.username}
                onChange={(e) => setAddUserForm({ ...addUserForm, username: e.target.value })}
                placeholder="Enter username"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={addUserForm.email}
                onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={addUserForm.password}
                onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={addUserForm.role}
                onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value, teacherType: 'regular', specialtySubject: '' })}
              >
                <option value="Teacher">Teacher</option>
                <option value="Admin">Admin</option>
                <option value="Parent">Parent</option>
              </select>
            </div>
            {addUserForm.role === 'Teacher' && (
              <>
                <div className="form-group">
                  <label>Teacher Type</label>
                  <select
                    value={addUserForm.teacherType}
                    onChange={(e) => setAddUserForm({ ...addUserForm, teacherType: e.target.value, specialtySubjects: [] })}
                  >
                    <option value="regular">Regular (Home Classroom)</option>
                    <option value="subject">Subject/Activity Teacher</option>
                  </select>
                </div>
                {addUserForm.teacherType === 'subject' && (
                  <div className="form-group">
                    <label>Specialty Subjects (select one or more)</label>
                    <div className="subjects-checkboxes">
                      {availableSubjects.map(subject => (
                        <label key={subject} className="subject-checkbox">
                          <input
                            type="checkbox"
                            value={subject}
                            checked={addUserForm.specialtySubjects.includes(subject)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAddUserForm({ ...addUserForm, specialtySubjects: [...addUserForm.specialtySubjects, subject] });
                              } else {
                                setAddUserForm({ ...addUserForm, specialtySubjects: addUserForm.specialtySubjects.filter(s => s !== subject) });
                              }
                            }}
                          />
                          {subject}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="modal-actions">
              <button onClick={handleCancelAddUser} className="cancel-button">Cancel</button>
              <button onClick={handleAddUser} className="save-button">Add User</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SuperAdminUsersView;
